import { motion } from "framer-motion";
import { Truck, ShieldCheck, TrendingUp, ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const SERVICES = [
  {
    num: "01",
    icon: Truck,
    title: "B2B IT Asset Collection",
    description:
      "White-glove removal of end-of-life laptops, servers, and network switches directly from your office or loading dock.",
    tag: "Logistics",
    testId: "service-card-collection",
  },
  {
    num: "02",
    icon: ShieldCheck,
    title: "Certified Data Destruction",
    description:
      "Military-grade data wiping and physical hard drive destruction, backed by verifiable Certificates of Destruction.",
    tag: "Security",
    testId: "service-card-destruction",
  },
  {
    num: "03",
    icon: TrendingUp,
    title: "Asset Buyback Program",
    description:
      "Turn retired tech into revenue. We offer competitive buyback rates for high-value 3-5 year old corporate IT assets.",
    tag: "Value Recovery",
    testId: "service-card-buyback",
  },
];

export default function Services() {
  return (
    <section id="services" className="relative bg-white py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mb-16 grid gap-8 lg:grid-cols-12 lg:items-end">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="lg:col-span-7"
          >
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-eco-dark" data-testid="services-overline">
              Chapter 01 — Core Services
            </p>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl" data-testid="services-heading">
              Every asset retired.
              <br />
              <span className="text-slate-400">Every byte accounted for.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="text-base leading-relaxed text-slate-600 lg:col-span-5 lg:pb-1"
          >
            From dock to destruction certificate, Revanta handles the full lifecycle of your
            corporate IT — securely, compliantly, and with maximum value recovered.
          </motion.p>
        </div>

        <div className="grid gap-6 md:grid-cols-3" data-testid="services-grid">
          {SERVICES.map((service, i) => (
            <motion.article
              key={service.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
              whileHover={{ y: -8 }}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-8 transition-[box-shadow,border-color] duration-500 hover:border-eco/40 hover:shadow-[0_24px_60px_rgba(4,10,7,0.12)] sm:p-10"
              data-testid={service.testId}
            >
              <div
                aria-hidden
                className="absolute -right-6 -top-8 select-none font-display text-[7rem] font-black leading-none text-slate-200/70 transition-colors duration-500 group-hover:text-eco/15"
              >
                {service.num}
              </div>

              <div className="relative">
                <span className="mb-8 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-navy shadow-lg transition-[background-color,transform] duration-500 group-hover:bg-eco group-hover:rotate-6">
                  <service.icon className="h-7 w-7 text-eco transition-colors duration-500 group-hover:text-navy" strokeWidth={1.8} />
                </span>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-eco-dark">
                  {service.tag}
                </p>
                <h3 className="font-display text-xl font-semibold tracking-tight text-navy sm:text-2xl">
                  {service.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
                  {service.description}
                </p>
              </div>

              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`${service.testId}-cta`}
                className="relative mt-10 inline-flex items-center gap-2 text-sm font-bold text-navy transition-colors duration-300 hover:text-eco-dark"
              >
                Request this service
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
